import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

function getStorageValue<T>(key: string, defaultValue?: T): T | undefined {
  // getting stored value. This won't work when running react_component from rails
  const saved = typeof window !== 'undefined' && localStorage.getItem(key);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      // cast: unparseable data returns the raw string as-is
      return saved as unknown as T;
    }
  } else if (defaultValue) {
    if (typeof window !== 'undefined') localStorage.setItem(key, JSON.stringify(defaultValue));
    return defaultValue;
  }
}

// any default: stored values are consumer-defined; T stays any unless inferable from defaultValue
export const useLocalStorage = <T = any>(key: string, defaultValue?: T): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T | undefined>(() => {
    return getStorageValue<T>(key, defaultValue);
  });
  // cast below: value is undefined until first set when no stored/default value exists (preserved runtime behavior)

  useEffect(() => {
    // storing input name
    if (value !== undefined && typeof window !== 'undefined') {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }, [key, value]);

  return [value, setValue] as [T, Dispatch<SetStateAction<T>>];
};
